import { Layout } from 'antd';
import { useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../modules/dashboard/hooks/useDashboard';
import {
  useAdminNotificationActions,
  useAdminNotifications,
} from '../hooks/useAdminNotifications';
import { asItems, firstDefined } from '../utils/pick';
import Sidebar from './Sidebar';
import AppHeader from './Header';

const { Content } = Layout;

function buildNotificationItems(data, t, navigate) {
  const kpis = data?.kpis || {};
  const quickLinks = firstDefined(data, ['quickLinks'], []);
  if (Array.isArray(quickLinks) && quickLinks.length) {
    return quickLinks
      .filter((link) => Number(link.count ?? 0) > 0)
      .map((link) => ({
        key: link.type || link.href,
        title: link.label || link.type,
        count: Number(link.count || 0),
        onClick: () => link.href && navigate(link.href),
      }));
  }

  return [
    {
      key: 'pendingApprovals',
      title: t('dashboard.pendingApprovals'),
      count: Number(kpis.pendingSupplierApprovals ?? 0),
      onClick: () => navigate('/onboarding'),
    },
    {
      key: 'openDisputes',
      title: t('dashboard.openDisputes'),
      count: Number(kpis.openDisputes ?? 0),
      onClick: () => navigate('/disputes'),
    },
    {
      key: 'overdueInvoices',
      title: t('dashboard.overdueInvoices'),
      count: Number(kpis.overdueInvoices ?? 0),
      onClick: () => navigate('/invoices?status=OVERDUE'),
    },
  ].filter((item) => item.count > 0);
}

function pathForNotification(item) {
  const type = firstDefined(item, ['entityType']);
  const id = firstDefined(item, ['entityId', 'entityNumber']);
  if (!id) return null;
  switch (type) {
    case 'DISPUTE':
      return `/disputes/${id}`;
    case 'CONTRACT':
      return `/contracts/${id}`;
    case 'INVOICE':
      return `/invoices/${id}`;
    case 'PAYMENT':
      return `/payments/${id}`;
    case 'DELIVERY':
      return `/deliveries/${id}`;
    case 'TICKET':
      return `/support/tickets/${id}`;
    default:
      return null;
  }
}

export default function AdminLayout({ routes }) {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useDashboard();
  const { data: notificationsData, isLoading: notificationsLoading } = useAdminNotifications({
    page: 1,
    size: 10,
    tab: 'UNREAD',
  });
  const { markRead, markAllRead } = useAdminNotificationActions();

  const notificationItems = useMemo(
    () => buildNotificationItems(data, t, navigate),
    [data, t, navigate],
  );

  const notificationMessages = useMemo(() => {
    return asItems(notificationsData).map((item) => ({
      id: item.id,
      title: firstDefined(item, ['title']),
      body: firstDefined(item, ['body']),
      read: Boolean(item.read),
      onClick: async () => {
        const path = pathForNotification(item);
        if (!item.read) {
          try {
            await markRead.mutateAsync(item.id);
          } catch {
            // navigation still useful if mark-read fails
          }
        }
        if (path) navigate(path);
      },
    }));
  }, [notificationsData, navigate, markRead.mutateAsync]);

  return (
    <Layout className="admin-layout">
      <Sidebar routes={routes} collapsed={collapsed} onCollapse={setCollapsed} />
      <Layout className="admin-layout__main">
        <AppHeader
          collapsed={collapsed}
          onToggleSidebar={() => setCollapsed((value) => !value)}
          notificationItems={notificationItems}
          notificationMessages={notificationMessages}
          notificationsLoading={isLoading || notificationsLoading}
          onMarkAllNotificationsRead={() => markAllRead.mutate()}
          markAllNotificationsLoading={markAllRead.isPending}
        />
        <Content className="app-content">
          <div className="route-fade">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
